// src/pages/Collection.jsx
import React from 'react';
import styled from 'styled-components';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PublicAPI } from '../api/public';
import { useCurrencyStore } from '../store/useCurrencyStore';
import { useAppStore } from '../store/useAppStore';
import { normalizeMediaUrl } from '../api/base';
import ListingCard from '../components/ListingCard';
import { renderTextWithLineBreaks } from '../utils/textHelpers';

const Page = styled.main`padding:18px 0;`;
const Grid4 = styled.div`
  display:grid; gap:16px; grid-template-columns: repeat(4, 1fr);
  @media (max-width:1200px){ grid-template-columns: repeat(3, 1fr); }
  @media (max-width:880px){ grid-template-columns: repeat(2, 1fr); }
  @media (max-width:560px){ grid-template-columns: 1fr; }
`;

export default function CollectionPage(){
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const currencyCode = (useCurrencyStore?.getState?.()?.code) || 'AED';
  const regionSlug = useAppStore(s => s.regionSlug);

  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number(searchParams.get('page') || 1);

  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  const load = React.useCallback(async ()=>{
    setLoading(true); setErr('');
    try{
      const res = await PublicAPI.collectionDetail({
        slug,
        locale: i18n.language || 'ru',
        currency: currencyCode || 'AED',
        region: regionSlug || undefined,
        page,
        page_size: 24,
      });
      setData(res);
    }catch(e){ setErr(e.message || 'Error'); }
    finally{ setLoading(false); }
  }, [slug, i18n.language, currencyCode, regionSlug, page]);

  React.useEffect(()=>{ load(); }, [load]);

  const nextPage = ()=> setSearchParams({ page: String(page + 1) });
  const prevPage = ()=> setSearchParams({ page: String(Math.max(1, page - 1)) });

  if (err) return <Page><div className="container" style={{color:'#c62828'}}>{err}</div></Page>;

  // --- Унификация ответа (поддержка двух вариантов бекенда) ---
  const coll = data?.collection ?? data ?? {};
  const title = coll?.title ?? '';
  const desc  = coll?.description ?? '';
  const cover = normalizeMediaUrl(coll?.cover || coll?.cover_url || coll?.cover_thumb || '');

  const itemsRaw = data?.items ?? coll?.items ?? [];
  // нормализуем: [{property:{...}}] -> [{...}]
  const items = itemsRaw.map(it => it?.property ?? it);

  // pages: если бекенд вернул pagination — используем его; иначе прикидываем из items_count
  const pagination = data?.pagination ?? null;
  const pages = pagination?.pages ?? (
    Math.max(1, Math.ceil((data?.items_count ?? items.length) / 24))
  );

  return (
    <Page>
      <div className="container">
        <h1 style={{margin:'0 0 10px'}}>{title}</h1>
        {desc && (
          <div 
            style={{opacity:.8, margin:'-6px 0 14px'}}
            dangerouslySetInnerHTML={renderTextWithLineBreaks(desc, true)}
          />
        )}
        {cover && <img src={cover} alt={title} style={{borderRadius:16, margin:'0 0 14px'}} />}

        {loading && <div style={{opacity:.7}}>Загрузка…</div>}

        {!loading && !items.length ? (
          <p>{t('collections.emptyItems','В этой подборке пока нет объектов')}</p>
        ) : (
          <Grid4>
            {items.map(p => {
              // подстрахуем обложку, если её нет на верхнем уровне
              const thumb = normalizeMediaUrl(
                p.cover_thumb || p.cover_url || p.cover || p.images?.[0]?.thumbnail_url || p.images?.[0]?.url || ''
              );
              const slugOrId = p.slug || p.id;
              // ListingCard сам берёт нужные поля; если он ждёт cover_url — подкинем через спред:
              const itemForCard = { cover_thumb: thumb, cover_url: p.cover_url || thumb, ...p };
              return (
                <Link key={slugOrId} to={`/realty/${slugOrId}`} style={{display:'block'}}>
                  <ListingCard item={itemForCard} lng={i18n.language} />
                </Link>
              );
            })}
          </Grid4>
        )}

        {/* Пагинация */}
        {pages > 1 && (
          <div style={{display:'flex', gap:8, marginTop:16}}>
            <button className="btn" onClick={prevPage} disabled={page <= 1}>
              {t('common.prev','Назад')}
            </button>
            <div style={{padding:'10px 14px', opacity:.7}}>
              {t('common.pageOf','Стр. {{p}} из {{n}}', { p: page, n: pages })}
            </div>
            <button className="btn" onClick={nextPage} disabled={page >= pages}>
              {t('common.next','Вперёд')}
            </button>
          </div>
        )}

        <div style={{marginTop:16}}>
          <Link className="btn btn-sm" to="/collections">{t('home.collectionsAll','Все подборки')}</Link>
        </div>
      </div>
    </Page>
  );
}
